import { UserInput, Vibe } from '../types.js';

export interface TripTemplate {
  id: string;
  name: string;
  destination: string;
  duration: string;
  category: 'romantic' | 'adventure' | 'wellness' | 'cultural' | 'family' | 'foodie';
  description: string;
  highlights: string[];
  prefilledInput: Partial<UserInput>;
  heroImage: string;
  priceRange: string;
  bestSeason: string;
  featured: boolean;
}

export const TEMPLATE_CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'romantic', label: 'Romantic' },
  { key: 'adventure', label: 'Adventure' },
  { key: 'wellness', label: 'Wellness' },
  { key: 'cultural', label: 'Cultural' },
  { key: 'family', label: 'Family' },
  { key: 'foodie', label: 'Foodie' },
] as const;

export const TRIP_TEMPLATES: TripTemplate[] = [
  {
    id: 'paris-romantic',
    name: 'Parisian Romance',
    destination: 'Paris, France',
    duration: '5 nights',
    category: 'romantic',
    description: 'A lovers\' escape through the City of Light — from Montmartre sunsets to Seine-side suppers.',
    highlights: [
      'Private Eiffel Tower dinner',
      'Champagne cruise on the Seine',
      'Hidden wine bars of Le Marais',
      'Sunrise at Sacr\u00e9-C\u0153ur',
    ],
    prefilledInput: {
      destination: 'Paris, France',
      startDate: getFutureDate(60),
      endDate: getFutureDate(65),
      vibe: [Vibe.ArtsCulture, Vibe.FoodieFocused],
      budgetTier: 5,
      auraDescription: 'Romantic, intimate dining, art galleries, boutique hotels, wine experiences',
    },
    heroImage: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Spring & Fall',
    featured: true,
  },
  {
    id: 'bali-wellness',
    name: 'Bali Wellness Retreat',
    destination: 'Bali, Indonesia',
    duration: '7 nights',
    category: 'wellness',
    description: 'A soul-restoring journey through Ubud\'s rice terraces, sacred temples, and world-class spas.',
    highlights: [
      'Private yoga at sunrise over rice paddies',
      'Traditional Balinese healing ceremony',
      'Jungle spa at Hanging Gardens',
      'Volcanic hot springs under the stars',
    ],
    prefilledInput: {
      destination: 'Bali, Indonesia',
      startDate: getFutureDate(75),
      endDate: getFutureDate(82),
      vibe: [Vibe.HealthWellness, Vibe.Adventure],
      budgetTier: 4,
      auraDescription: 'Wellness, spa, yoga, healthy cuisine, nature, spiritual experiences, luxury villas',
    },
    heroImage: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Apr\u2013Oct',
    featured: true,
  },
  {
    id: 'tokyo-cultural',
    name: 'Tokyo Cultural Immersion',
    destination: 'Tokyo, Japan',
    duration: '6 nights',
    category: 'cultural',
    description: 'Ancient traditions meet neon-lit futures in a city where every corner tells a different story.',
    highlights: [
      'Private tea ceremony in a hidden machiya',
      'Dawn tuna auction at Toyosu Market',
      'After-hours tour of teamLab Borderless',
      'Omakase dinner in a 6-seat Ginza sushi bar',
    ],
    prefilledInput: {
      destination: 'Tokyo, Japan',
      startDate: getFutureDate(90),
      endDate: getFutureDate(96),
      vibe: [Vibe.ArtsCulture, Vibe.FoodieFocused],
      budgetTier: 4,
      auraDescription: 'Cultural immersion, traditional arts, cutting-edge design, world-class cuisine, hidden local gems',
    },
    heroImage: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Spring & Fall',
    featured: true,
  },
  {
    id: 'amalfi-luxury',
    name: 'Amalfi Coast La Dolce Vita',
    destination: 'Amalfi Coast, Italy',
    duration: '5 nights',
    category: 'romantic',
    description: 'Sun-drenched cliffs, limoncello terraces, and sapphire waters along Italy\'s most storied coastline.',
    highlights: [
      'Private yacht from Positano to Capri',
      'Cooking class in a cliffside villa',
      'Sunset aperitivo at Hotel Le Sirenuse',
      'Hidden beach cove accessible only by boat',
    ],
    prefilledInput: {
      destination: 'Amalfi Coast, Italy',
      startDate: getFutureDate(70),
      endDate: getFutureDate(75),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 5,
      auraDescription: 'Mediterranean luxury, coastal romance, Italian gastronomy, boutique cliff-side hotels, yacht excursions',
    },
    heroImage: 'https://images.unsplash.com/photo-1534008897995-27a23e859048?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'May\u2013Sep',
    featured: true,
  },
  {
    id: 'iceland-adventure',
    name: 'Iceland Untamed',
    destination: 'Reykjavik & Golden Circle, Iceland',
    duration: '4 nights',
    category: 'adventure',
    description: 'Glaciers, geysers, and the Northern Lights in one of Earth\'s last truly wild frontiers.',
    highlights: [
      'Ice cave expedition inside Vatnaj\u00f6kull glacier',
      'Northern Lights hunt by super jeep',
      'Geothermal spa in the Sky Lagoon',
      'Snorkelling between tectonic plates at Silfra',
    ],
    prefilledInput: {
      destination: 'Reykjavik & Golden Circle, Iceland',
      startDate: getFutureDate(100),
      endDate: getFutureDate(104),
      vibe: [Vibe.Adventure, Vibe.HealthWellness],
      budgetTier: 4,
      auraDescription: 'Adventure, raw nature, Northern Lights, geothermal spas, glacier hikes, off-road exploration',
    },
    heroImage: 'https://images.unsplash.com/photo-1520769945061-0a448c463865?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Sep\u2013Mar',
    featured: false,
  },
  {
    id: 'marrakech-foodie',
    name: 'Marrakech Culinary Journey',
    destination: 'Marrakech, Morocco',
    duration: '4 nights',
    category: 'foodie',
    description: 'A feast for every sense \u2014 saffron-scented souks, rooftop tagines, and spice-laden adventures in the Red City.',
    highlights: [
      'Private spice market tour with a local chef',
      'Hands-on pastilla cooking in a riad kitchen',
      'Midnight street food crawl through Jemaa el-Fna',
      'Atlas Mountains lunch with a Berber family',
    ],
    prefilledInput: {
      destination: 'Marrakech, Morocco',
      startDate: getFutureDate(80),
      endDate: getFutureDate(84),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 3,
      auraDescription: 'Culinary exploration, spice markets, traditional Moroccan cuisine, riad living, cultural immersion',
    },
    heroImage: 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Mar\u2013May & Oct\u2013Nov',
    featured: false,
  },
  {
    id: 'maldives-escape',
    name: 'Maldives Overwater Escape',
    destination: 'Maldives',
    duration: '6 nights',
    category: 'wellness',
    description: 'Crystal lagoons, overwater villas, and absolute serenity on the edge of the Indian Ocean.',
    highlights: [
      'Overwater villa with private infinity pool',
      'Bioluminescent night snorkelling',
      'Underwater spa treatment at Huvafen Fushi',
      'Castaway sandbank picnic at sunset',
    ],
    prefilledInput: {
      destination: 'Maldives',
      startDate: getFutureDate(85),
      endDate: getFutureDate(91),
      vibe: [Vibe.HealthWellness, Vibe.Adventure],
      budgetTier: 5,
      auraDescription: 'Ultimate relaxation, overwater luxury, marine life, spa rituals, private island seclusion',
    },
    heroImage: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Nov\u2013Apr',
    featured: true,
  },
  {
    id: 'swiss-alps',
    name: 'Swiss Alps Family Adventure',
    destination: 'Interlaken & Zermatt, Switzerland',
    duration: '5 nights',
    category: 'family',
    description: 'Chocolate factories, Alpine railways, and Matterhorn magic \u2014 a family journey through Switzerland\'s finest.',
    highlights: [
      'Jungfraujoch \u201cTop of Europe\u201d railway',
      'Swiss chocolate-making workshop for kids',
      'Paragliding tandem over Lake Brienz',
      'Glacier Express panoramic rail journey',
    ],
    prefilledInput: {
      destination: 'Interlaken & Zermatt, Switzerland',
      startDate: getFutureDate(95),
      endDate: getFutureDate(100),
      vibe: [Vibe.Family, Vibe.Adventure],
      budgetTier: 4,
      auraDescription: 'Family-friendly adventure, Alpine scenery, Swiss chocolate, scenic railways, mountain activities',
    },
    heroImage: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Jun\u2013Sep & Dec\u2013Mar',
    featured: false,
  },
  // --- Additional templates to reach 4 per category ---
  {
    id: 'maldives-romantic',
    name: 'Maldives Overwater Escape',
    destination: 'Maldives',
    duration: '6 nights',
    category: 'romantic',
    description: 'Crystal lagoons, private overwater villas, and barefoot luxury on the world\'s most exclusive atolls.',
    highlights: [
      'Overwater villa with glass floor',
      'Sunset dolphin cruise',
      'Private sandbank picnic',
      'Couples underwater spa treatment',
    ],
    prefilledInput: {
      destination: 'Maldives',
      startDate: getFutureDate(70),
      endDate: getFutureDate(76),
      vibe: [Vibe.HealthWellness, Vibe.Adventure],
      budgetTier: 5,
      auraDescription: 'Ultra-luxury, overwater villa, romantic, beach, snorkeling, private dining',
    },
    heroImage: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Nov–Apr',
    featured: false,
  },
  {
    id: 'amalfi-romantic',
    name: 'Amalfi Coast by Sea',
    destination: 'Amalfi Coast, Italy',
    duration: '5 nights',
    category: 'romantic',
    description: 'La Dolce Vita reimagined — cliffside villages, lemon groves, and private boat charters along Italy\'s most dramatic coastline.',
    highlights: [
      'Private yacht along the coast',
      'Limoncello-making at a hillside farm',
      'Candlelit dinner in Ravello',
      'Hidden beach accessible only by boat',
    ],
    prefilledInput: {
      destination: 'Amalfi Coast, Italy',
      startDate: getFutureDate(55),
      endDate: getFutureDate(60),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 5,
      auraDescription: 'Romantic Italian coast, private boats, cliffside dining, limoncello, Mediterranean luxury',
    },
    heroImage: 'https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'May–Oct',
    featured: false,
  },
  {
    id: 'patagonia-adventure',
    name: 'Patagonia Expedition',
    destination: 'Torres del Paine, Chile',
    duration: '8 nights',
    category: 'adventure',
    description: 'Glaciers, granite towers, and gaucho culture at the edge of the world — Patagonia\'s ultimate wilderness circuit.',
    highlights: [
      'W Trek through Torres del Paine',
      'Glacier kayaking on Grey Lake',
      'Horseback riding with gauchos',
      'Luxury eco-lodge with mountain views',
    ],
    prefilledInput: {
      destination: 'Torres del Paine, Patagonia, Chile',
      startDate: getFutureDate(120),
      endDate: getFutureDate(128),
      vibe: [Vibe.Adventure, Vibe.HealthWellness],
      budgetTier: 4,
      auraDescription: 'Patagonia trekking, glaciers, wilderness, luxury eco-lodge, horseback riding, dramatic landscapes',
    },
    heroImage: 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Oct–Mar',
    featured: false,
  },
  {
    id: 'iceland-ring-road',
    name: 'Iceland Ring Road',
    destination: 'Iceland',
    duration: '9 nights',
    category: 'adventure',
    description: 'Volcanic black beaches, cascading waterfalls, and the Northern Lights — a self-drive odyssey around the Land of Fire and Ice.',
    highlights: [
      'Northern Lights from a geothermal lagoon',
      'Glacier hiking on Vatnajökull',
      'Whale watching from Húsavík',
      'Secret hot springs off the beaten path',
    ],
    prefilledInput: {
      destination: 'Iceland (Ring Road)',
      startDate: getFutureDate(100),
      endDate: getFutureDate(109),
      vibe: [Vibe.Adventure, Vibe.HealthWellness],
      budgetTier: 4,
      auraDescription: 'Self-drive Iceland, Northern Lights, waterfalls, glaciers, hot springs, volcanic landscapes',
    },
    heroImage: 'https://images.unsplash.com/photo-1504829857797-ddff29c27927?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Sep–Mar (lights) / Jun–Aug (midnight sun)',
    featured: false,
  },
  {
    id: 'safari-adventure',
    name: 'East Africa Safari',
    destination: 'Kenya & Tanzania',
    duration: '10 nights',
    category: 'adventure',
    description: 'The Great Migration, Big Five encounters, and Maasai culture — Africa\'s iconic safari experience at the highest level.',
    highlights: [
      'Hot air balloon over the Serengeti',
      'Big Five game drives in the Masai Mara',
      'Ngorongoro Crater descent',
      'Private bush dinner under the Milky Way',
    ],
    prefilledInput: {
      destination: 'Masai Mara, Kenya & Serengeti, Tanzania',
      startDate: getFutureDate(85),
      endDate: getFutureDate(95),
      vibe: [Vibe.Adventure, Vibe.ArtsCulture],
      budgetTier: 5,
      auraDescription: 'Luxury safari, Big Five, Great Migration, bush camps, Maasai culture, balloon rides',
    },
    heroImage: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Jul–Oct (migration)',
    featured: false,
  },
  {
    id: 'rajasthan-cultural',
    name: 'Royal Rajasthan',
    destination: 'Rajasthan, India',
    duration: '8 nights',
    category: 'cultural',
    description: 'Maharaja palaces, desert fortresses, and the pink city — India\'s most regal and colorful state.',
    highlights: [
      'Stay in a converted maharaja palace',
      'Camel safari in the Thar Desert',
      'Private textile workshop in Jaipur',
      'Sunset at Mehrangarh Fort, Jodhpur',
    ],
    prefilledInput: {
      destination: 'Rajasthan, India (Jaipur, Jodhpur, Udaipur)',
      startDate: getFutureDate(90),
      endDate: getFutureDate(98),
      vibe: [Vibe.ArtsCulture, Vibe.Adventure],
      budgetTier: 4,
      auraDescription: 'Royal palaces, desert forts, Indian textiles, spice markets, heritage hotels, Rajasthani cuisine',
    },
    heroImage: 'https://images.unsplash.com/photo-1548013146-72479768bada?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Oct–Mar',
    featured: false,
  },
  {
    id: 'morocco-cultural',
    name: 'Morocco: Medinas & Mountains',
    destination: 'Morocco',
    duration: '7 nights',
    category: 'cultural',
    description: 'From the labyrinthine souks of Marrakech to the silence of the Sahara — Morocco\'s sensory overload at its finest.',
    highlights: [
      'Private guided tour of Fes medina',
      'Luxury desert camp under the stars',
      'Cooking class in a Marrakech riad',
      'Atlas Mountain village visit',
    ],
    prefilledInput: {
      destination: 'Morocco (Marrakech, Fes, Sahara)',
      startDate: getFutureDate(65),
      endDate: getFutureDate(72),
      vibe: [Vibe.ArtsCulture, Vibe.Adventure],
      budgetTier: 3,
      auraDescription: 'Moroccan riads, souks, Sahara desert, Atlas Mountains, tagine cooking, cultural immersion',
    },
    heroImage: 'https://images.unsplash.com/photo-1549924231-f129b911e442?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Refined',
    bestSeason: 'Mar–May & Sep–Nov',
    featured: false,
  },
  {
    id: 'peru-cultural',
    name: 'Peru: Inca Trail & Beyond',
    destination: 'Peru',
    duration: '8 nights',
    category: 'cultural',
    description: 'Machu Picchu, Sacred Valley, and Lima\'s culinary revolution — ancient civilization meets modern gastronomy.',
    highlights: [
      'Sunrise at Machu Picchu',
      'Sacred Valley weaving workshop',
      'Lima food tour with a Michelin chef',
      'Lake Titicaca floating islands',
    ],
    prefilledInput: {
      destination: 'Peru (Lima, Cusco, Sacred Valley, Machu Picchu)',
      startDate: getFutureDate(80),
      endDate: getFutureDate(88),
      vibe: [Vibe.ArtsCulture, Vibe.FoodieFocused],
      budgetTier: 4,
      auraDescription: 'Inca ruins, Machu Picchu, Peruvian cuisine, Sacred Valley, weaving, cultural heritage',
    },
    heroImage: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'May–Sep',
    featured: false,
  },
  {
    id: 'costa-rica-wellness',
    name: 'Costa Rica Pura Vida',
    destination: 'Costa Rica',
    duration: '7 nights',
    category: 'wellness',
    description: 'Rainforest canopy walks, volcanic hot springs, and Pacific coast yoga — Costa Rica\'s ultimate reset.',
    highlights: [
      'Arenal volcano hot springs at sunset',
      'Beachfront yoga at sunrise',
      'Rainforest zip-lining and wildlife',
      'Farm-to-table cooking in the cloud forest',
    ],
    prefilledInput: {
      destination: 'Costa Rica (Arenal, Monteverde, Guanacaste)',
      startDate: getFutureDate(70),
      endDate: getFutureDate(77),
      vibe: [Vibe.HealthWellness, Vibe.Adventure],
      budgetTier: 3,
      auraDescription: 'Wellness retreat, yoga, hot springs, rainforest, wildlife, sustainable eco-lodges',
    },
    heroImage: 'https://images.unsplash.com/photo-1621451537084-482c73073a0f?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Refined',
    bestSeason: 'Dec–Apr',
    featured: false,
  },
  {
    id: 'sedona-wellness',
    name: 'Sedona Spiritual Reset',
    destination: 'Sedona, Arizona',
    duration: '4 nights',
    category: 'wellness',
    description: 'Red rock vortexes, desert stargazing, and luxury spa immersion in Arizona\'s most mystical landscape.',
    highlights: [
      'Guided vortex energy hike',
      'Sound healing in a red rock canyon',
      'Stargazing with a private astronomer',
      'Desert spa with indigenous botanicals',
    ],
    prefilledInput: {
      destination: 'Sedona, Arizona, USA',
      startDate: getFutureDate(45),
      endDate: getFutureDate(49),
      vibe: [Vibe.HealthWellness, Vibe.Adventure],
      budgetTier: 4,
      auraDescription: 'Spiritual wellness, desert spa, vortex energy, stargazing, red rock hiking, meditation',
    },
    heroImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Mar–May & Sep–Nov',
    featured: false,
  },
  {
    id: 'london-family',
    name: 'London Family Discovery',
    destination: 'London, England',
    duration: '5 nights',
    category: 'family',
    description: 'Harry Potter studios, West End shows, and royal palaces — London through the eyes of wonder.',
    highlights: [
      'Warner Bros. Harry Potter Studio Tour',
      'West End musical matinee',
      'Changing of the Guard at Buckingham Palace',
      'Natural History Museum & Science Museum',
    ],
    prefilledInput: {
      destination: 'London, England',
      startDate: getFutureDate(60),
      endDate: getFutureDate(65),
      vibe: [Vibe.Family, Vibe.ArtsCulture],
      budgetTier: 4,
      auraDescription: 'Family-friendly London, Harry Potter, theater, museums, royal history, afternoon tea',
    },
    heroImage: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Apr–Jun & Sep–Oct',
    featured: false,
  },
  {
    id: 'disney-family',
    name: 'Orlando Theme Park Epic',
    destination: 'Orlando, Florida',
    duration: '6 nights',
    category: 'family',
    description: 'The ultimate theme park adventure — Disney, Universal, and beyond, with VIP access and zero lines.',
    highlights: [
      'VIP tour guide at Magic Kingdom',
      'Wizarding World of Harry Potter',
      'Fireworks dessert party',
      'Kennedy Space Center day trip',
    ],
    prefilledInput: {
      destination: 'Orlando, Florida, USA',
      startDate: getFutureDate(50),
      endDate: getFutureDate(56),
      vibe: [Vibe.Family, Vibe.Adventure],
      budgetTier: 4,
      auraDescription: 'Theme parks, Disney VIP, Universal Studios, family dining, pool resort, space center',
    },
    heroImage: 'https://images.unsplash.com/photo-1597466599360-3b9775841aec?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Feb–Mar & Sep–Nov',
    featured: false,
  },
  {
    id: 'costa-brava-family',
    name: 'Costa Brava Family Sun',
    destination: 'Costa Brava, Spain',
    duration: '7 nights',
    category: 'family',
    description: 'Dalí museums, hidden coves, and the best paella in Catalonia — Mediterranean family adventure at its finest.',
    highlights: [
      'Dalí Theatre-Museum in Figueres',
      'Kayaking to hidden beach coves',
      'Paella cooking class for the whole family',
      'Medieval village of Pals exploration',
    ],
    prefilledInput: {
      destination: 'Costa Brava, Spain',
      startDate: getFutureDate(75),
      endDate: getFutureDate(82),
      vibe: [Vibe.Family, Vibe.FoodieFocused],
      budgetTier: 3,
      auraDescription: 'Family beach holiday, Dalí art, kayaking, Spanish cuisine, medieval villages, Mediterranean',
    },
    heroImage: 'https://images.unsplash.com/photo-1543783207-ec64e4d95325?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Refined',
    bestSeason: 'Jun–Sep',
    featured: false,
  },
  {
    id: 'lyon-foodie',
    name: 'Lyon Gastronomic Trail',
    destination: 'Lyon, France',
    duration: '4 nights',
    category: 'foodie',
    description: 'The gastronomic capital of France — Michelin stars, bouchon traditions, and the finest Beaujolais.',
    highlights: [
      'Michelin-starred tasting menu at Paul Bocuse',
      'Les Halles de Lyon Paul Bocuse market tour',
      'Beaujolais vineyard half-day trip',
      'Traditional bouchon crawl in Vieux Lyon',
    ],
    prefilledInput: {
      destination: 'Lyon, France',
      startDate: getFutureDate(55),
      endDate: getFutureDate(59),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 4,
      auraDescription: 'French gastronomy, Michelin dining, wine tasting, Beaujolais, food markets, bouchon',
    },
    heroImage: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Premium',
    bestSeason: 'Apr–Jun & Sep–Nov',
    featured: false,
  },
  {
    id: 'mexico-city-foodie',
    name: 'Mexico City Street Food & Fine Dining',
    destination: 'Mexico City, Mexico',
    duration: '5 nights',
    category: 'foodie',
    description: 'From Oaxacan mole masters to Pujol\'s tasting counter — Mexico City\'s explosive culinary scene.',
    highlights: [
      'Tasting menu at Pujol (World\'s 50 Best)',
      'Street food tour through Roma Norte',
      'Mezcal masterclass in a speakeasy',
      'Xochimilco floating garden brunch',
    ],
    prefilledInput: {
      destination: 'Mexico City, Mexico',
      startDate: getFutureDate(65),
      endDate: getFutureDate(70),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 3,
      auraDescription: 'Mexican cuisine, street food, Michelin dining, mezcal, markets, Frida Kahlo, muralism',
    },
    heroImage: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Refined',
    bestSeason: 'Oct–May',
    featured: false,
  },
  {
    id: 'tokyo-foodie',
    name: 'Tokyo Culinary Odyssey',
    destination: 'Tokyo, Japan',
    duration: '6 nights',
    category: 'foodie',
    description: 'Tsukiji dawn tuna, Michelin ramen counters, and omakase at the world\'s greatest sushi bar.',
    highlights: [
      'Dawn tuna auction at Toyosu Market',
      'Omakase at a 6-seat Ginza sushi bar',
      'Depachika basement food hall crawl',
      'Sake tasting in a 300-year-old brewery',
    ],
    prefilledInput: {
      destination: 'Tokyo, Japan',
      startDate: getFutureDate(80),
      endDate: getFutureDate(86),
      vibe: [Vibe.FoodieFocused, Vibe.ArtsCulture],
      budgetTier: 5,
      auraDescription: 'Japanese cuisine, sushi omakase, ramen, sake, Tsukiji market, izakaya, Michelin stars',
    },
    heroImage: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=800&auto=format&fit=crop&fm=webp',
    priceRange: 'Ultra-Premium',
    bestSeason: 'Mar–May & Oct–Nov',
    featured: false,
  },
];

/** Returns a YYYY-MM-DD string `daysFromNow` days in the future. */
function getFutureDate(daysFromNow: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split('T')[0];
}
